<script setup lang="ts">
import { watch, ref, computed } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import { requiredRule } from '@/helpers/FormRules';
import {
  createProjectDeployment,
  listProjectTrainings,
} from '@/api/RobVisionApi';
import { loadRobotsSerialNumbers } from '@/api/RobotsApi';
import {
  TrainingStatus,
  DeploymentStatus,
  type ProjectTrainingRun,
  type VisionProject,
} from '@/models/RobVision';
import formatDate from '@/helpers/DateFormatterHelper';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

const props = defineProps<{
  isOpen: boolean;
  project: VisionProject;
  // Preselect this run in the version dropdown (row deploy); the newest
  // successful run is preselected otherwise.
  runId?: string | null;
}>();
const emit = defineEmits(['update:isOpen', 'deployment-created']);

interface ControlUnitRow {
  serialNumber: string;
  // Model version currently running there, from the runs' deployment lists.
  runsVersion?: number;
}

const loading = ref(false);
const deploying = ref(false);
const loadError = ref('');
const runs = ref<ProjectTrainingRun[]>([]);
const selectedRunId = ref<string | null>(null);
const controlUnits = ref<ControlUnitRow[]>([]);
const selectedSerials = ref<Set<string>>(new Set());
const unitFilter = ref('');
// Serials that failed in the last rollout attempt, with their error.
const failures = ref<Array<{ serialNumber: string; message: string }>>([]);

const succeededRuns = computed(() =>
  runs.value.filter(
    (run) => run.status === TrainingStatus.SUCCEEDED && !!run.runId,
  ),
);

const newestSucceededId = computed(() => succeededRuns.value[0]?.runId);

const versionOptions = computed(() =>
  succeededRuns.value.map((run) => {
    const name =
      run.name ||
      (run.baseModel === true
        ? $gettext('%{ name } base', { name: props.project.name })
        : run.baseModel === false
          ? $gettext('%{ name } refined', { name: props.project.name })
          : props.project.name);
    const kind =
      run.baseModel === true ? $gettext('Base') : $gettext('Fine-tuned');
    const date = run.createdAt ? formatDate(run.createdAt, true) : '';
    return {
      title: `${name} — ${kind}${date ? ` (${date})` : ''}`,
      value: run.runId as string,
    };
  }),
);

const isRollback = computed(
  () =>
    selectedRunId.value !== null &&
    newestSucceededId.value !== undefined &&
    selectedRunId.value !== newestSucceededId.value,
);

const canRollOut = computed(
  () =>
    !deploying.value &&
    selectedRunId.value !== null &&
    selectedSerials.value.size > 0,
);

/** serial → model version of the newest successful run deployed on it.
 * Runs come newest-first, so the first hit per serial wins. Failed and
 * in-flight deployments don't count — the CU doesn't run that model. */
function buildRunsVersionMap(): Map<string, number> {
  const map = new Map<string, number>();
  for (const run of succeededRuns.value) {
    if (run.modelVersion == null) {
      continue;
    }
    for (const deployment of run.deployments) {
      if (
        deployment.status === DeploymentStatus.Success &&
        !map.has(deployment.serialNumber)
      ) {
        map.set(deployment.serialNumber, run.modelVersion);
      }
    }
  }
  return map;
}

async function fetchData() {
  loading.value = true;
  loadError.value = '';
  try {
    const [projectRuns, serialNumbers] = await Promise.all([
      listProjectTrainings(props.project.uuid),
      loadRobotsSerialNumbers(),
    ]);
    runs.value = projectRuns;
    selectedRunId.value =
      props.runId &&
      succeededRuns.value.some((run) => run.runId === props.runId)
        ? props.runId
        : (newestSucceededId.value ?? null);
    const versionMap = buildRunsVersionMap();
    const serials = new Set(serialNumbers);
    // A serial with a deployment belongs in the list even when the user
    // lost access to it in the meantime.
    for (const serialNumber of versionMap.keys()) {
      serials.add(serialNumber);
    }
    // Units this project already runs on come first — they are the ones a
    // rollout typically updates.
    controlUnits.value = [...serials]
      .map((serialNumber) => ({
        serialNumber,
        runsVersion: versionMap.get(serialNumber),
      }))
      .sort(
        (a, b) =>
          Number(b.runsVersion != null) - Number(a.runsVersion != null) ||
          a.serialNumber.localeCompare(b.serialNumber),
      );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    loadError.value = error.message;
  } finally {
    loading.value = false;
  }
}

// Fleet users can have far more units than fit the list — filter by serial.
// Filtering only hides rows; selections made before filtering stay selected.
const filteredControlUnits = computed(() => {
  const query = unitFilter.value.trim().toLowerCase();
  if (!query) {
    return controlUnits.value;
  }
  return controlUnits.value.filter((unit) =>
    unit.serialNumber.toLowerCase().includes(query),
  );
});

// v-list's selection model is an array; the rollout logic keeps the Set.
const selectedSerialsList = computed({
  get: () => [...selectedSerials.value],
  set: (list: unknown[]) => {
    selectedSerials.value = new Set(list as string[]);
  },
});

async function rollOut() {
  if (!canRollOut.value || selectedRunId.value === null) {
    return;
  }
  deploying.value = true;
  failures.value = [];
  const succeeded: string[] = [];
  // Sequential on purpose: one deployment call per control unit, and a
  // failing unit must not abort the rest of the rollout.
  for (const serialNumber of selectedSerials.value) {
    try {
      await createProjectDeployment({
        projectUuid: props.project.uuid,
        serialNumber,
        runId: selectedRunId.value,
      });
      succeeded.push(serialNumber);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      failures.value.push({
        serialNumber,
        message: error.message || $gettext('Deployment failed'),
      });
    }
  }
  deploying.value = false;
  if (succeeded.length > 0) {
    emit('deployment-created');
  }
  if (failures.value.length === 0) {
    close();
    return;
  }
  // Keep only the failed units selected so "Roll out" retries exactly those.
  selectedSerials.value = new Set(
    failures.value.map((failure) => failure.serialNumber),
  );
}

function close() {
  emit('update:isOpen', false);
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      selectedSerials.value = new Set();
      unitFilter.value = '';
      failures.value = [];
      fetchData();
    }
  },
  // The view v-ifs this dialog into existence with isOpen already true.
  { immediate: true },
);
</script>

<template>
  <v-dialog :model-value="isOpen" max-width="600" @click:outside="close">
    <v-card>
      <v-card-title>
        {{ $gettext('Roll out to robot') }}: {{ project.name }}
      </v-card-title>
      <v-card-text>
        <div v-if="loading" class="d-flex justify-center py-8">
          <v-progress-circular indeterminate />
        </div>
        <template v-else>
          <v-alert v-if="loadError" type="error" variant="tonal" class="mb-4">
            {{ loadError }}
          </v-alert>
          <template v-else>
            <p class="text-caption text-medium-emphasis mb-1">
              {{ $gettext('1 · Choose a model version') }}
            </p>
            <v-select
              v-model="selectedRunId"
              :items="versionOptions"
              :label="$gettext('Model version — finished trainings')"
              :rules="[requiredRule]"
              :no-data-text="$gettext('No finished trainings yet')"
              density="comfortable"
            />
            <v-alert
              v-if="isRollback"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-4"
            >
              {{
                $gettext(
                  'You are deploying an older model version — a newer trained model exists for this project.',
                )
              }}
            </v-alert>

            <p class="text-caption text-medium-emphasis mb-1">
              {{ $gettext('2 · Choose one or more control units') }}
            </p>
            <v-text-field
              v-if="controlUnits.length > 5"
              v-model="unitFilter"
              :placeholder="$gettext('Search control units')"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              variant="outlined"
              rounded="lg"
              clearable
              hide-details
              class="mb-2"
            />
            <v-list
              v-model:selected="selectedSerialsList"
              select-strategy="leaf"
              density="compact"
              class="unit-list bg-transparent pt-0"
            >
              <v-list-item
                v-for="unit in filteredControlUnits"
                :key="unit.serialNumber"
                :value="unit.serialNumber"
                rounded="lg"
                border
                class="mb-2"
              >
                <template #prepend="{ isSelected }">
                  <v-checkbox-btn
                    :model-value="isSelected"
                    density="compact"
                    class="flex-grow-0 mr-2 unit-check"
                    tabindex="-1"
                  />
                </template>
                <v-list-item-title class="text-body-2">
                  <span class="font-weight-medium mr-2">
                    {{ unit.serialNumber }}
                  </span>
                  <v-chip
                    v-if="unit.runsVersion != null"
                    size="x-small"
                    density="comfortable"
                  >
                    {{ $gettext('runs v%{ n }', { n: `${unit.runsVersion}` }) }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item>
            </v-list>

            <v-alert
              v-if="failures.length > 0"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-4"
            >
              <p class="mb-1">
                {{
                  $gettext('Some deployments failed — still selected below:')
                }}
              </p>
              <p
                v-for="failure in failures"
                :key="failure.serialNumber"
                class="mb-0"
              >
                {{ failure.serialNumber }}: {{ failure.message }}
              </p>
            </v-alert>
          </template>
        </template>
      </v-card-text>
      <v-card-actions>
        <r-btn variant="outlined" @click="close">
          {{ $gettext('Close') }}
        </r-btn>
        <v-spacer />
        <r-btn
          variant="accent"
          icon="mdi-robot-industrial"
          :disabled="!canRollOut"
          :loading="deploying"
          @click="rollOut"
        >
          {{ $gettext('Roll out') }}
        </r-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.unit-list {
  max-height: 320px;
  overflow-y: auto;
}
/* Display-only: the list item owns the selection. Without this, a click on
   the box toggles only the checkbox's internal state and drifts from the
   real selection (looks checked, unit not selected). */
.unit-check {
  pointer-events: none;
}
</style>
